import React, { useState, useMemo, useEffect } from 'react';
import { Button, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiFilter, FiX, FiChevronDown, FiChevronUp, FiSearch, FiRefreshCw } from 'react-icons/fi';
const FilterSection = ({ title, count, onClear, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen || false);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);
  return (
    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
      <Button onClick={() => setOpen(o => !o)} variant="ghost">
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{title}</span>
          {count > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-5 tw-h-5 tw-px-1.5 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold">
              {count}
            </span>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-1.5">
          {count > 0 && onClear && (
            <span
              role="button"
              tabIndex={0}
              onClick={e => { e.stopPropagation(); onClear(); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); onClear(); } }}
              className="tw-text-xs tw-text-red-400 hover:tw-text-red-600 tw-font-medium tw-cursor-pointer tw-px-1 tw-rounded-sm hover:tw-bg-red-50 tw-transition-colors"
            >
              Clear
            </span>
          )}
          {open
            ? <FiChevronUp size={13} className="tw-text-gray-400" />
            : <FiChevronDown size={13} className="tw-text-gray-400" />}
        </div>
      </Button>
      {open && (
        <div className="tw-px-3 tw-py-2.5 tw-space-y-1 tw-max-h-56 tw-overflow-y-auto tw-bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

const CheckItem = ({ label, checked, onChange }) => (
  <label className="tw-flex tw-items-center tw-gap-2.5 tw-py-1.5 tw-px-2 tw-rounded hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="tw-w-4 tw-h-4 tw-rounded-sm tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer focus:tw-ring-2 focus:tw-ring-blue-500"
    />
    <span className="tw-text-sm tw-text-gray-700 tw-select-none tw-flex-1 tw-leading-snug">{label}</span>
  </label>
);

CheckItem.propTypes = {
  label: PropTypes.node,
  checked: PropTypes.bool,
  onChange: PropTypes.func
};

const SearchInput = ({ value, onChange, placeholder }) => (
  <div className="tw-relative tw-mb-2">
    <FiSearch className="tw-absolute tw-left-2.5 tw-top-2.5 tw-text-gray-400" size={13} />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="tw-absolute tw-right-2 tw-top-2 tw-text-gray-400 hover:tw-text-gray-600 tw-bg-transparent tw-border-0 tw-p-0 tw-leading-none tw-cursor-pointer"
      >
        <FiX size={11} />
      </button>
    )}
  </div>
);

SearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

const buildGroups = (units, search) => {
  const q = search.toLowerCase();
  const groups = {};
  units.forEach(unit => {
    const groupKey = unit.region || unit.name;
    const label    = [unit.name, unit.description].filter(Boolean).join(' - ');
    if (!groups[groupKey]) groups[groupKey] = [];
    if (!q || label.toLowerCase().includes(q) || groupKey.toLowerCase().includes(q)) {
      groups[groupKey].push(unit);
    }
  });
  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, items]) => ({ region, units: items.sort((a, b) => a.name.localeCompare(b.name)) }));
};

FilterSection.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  onClear: PropTypes.func,
  defaultOpen: PropTypes.bool,
  children: PropTypes.node
};
FilterSection.defaultProps = { count: 0, onClear: null, defaultOpen: false, children: null };

const SalesUnitFilter = ({ units, selected, search, onSearch, onToggle, onToggleGroup, onClear }) => {
  const groups = useMemo(() => buildGroups(units, search), [units, search]);
  return (
    <FilterSection title="Sales Unit" count={selected.length} onClear={onClear} defaultOpen={selected.length > 0}>
      {units.length > 0 ? (
        <>
          <SearchInput value={search} onChange={onSearch} placeholder="Search sales unit…" />
          {groups.map(({ region, units: groupUnits }) => {
            const ids    = groupUnits.map(u => u.id);
            const allOn  = ids.every(id => selected.includes(id));
            const someOn = !allOn && ids.some(id => selected.includes(id));
            return (
              <div key={region} className="tw-mb-1">
                <label className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1 tw-rounded-md tw-bg-gray-50 tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors">
                  <input
                    type="checkbox"
                    checked={allOn}
                    ref={el => { if (el) el.indeterminate = someOn; }}
                    onChange={() => onToggleGroup(ids)}
                    className="tw-w-4 tw-h-4 tw-rounded-sm tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer"
                  />
                  <span className="tw-text-xs tw-font-bold tw-text-blue-700 tw-uppercase tw-tracking-wide tw-select-none tw-flex-1">{region}</span>
                  <span className="tw-text-xs tw-text-gray-400">{ids.length}</span>
                </label>
                <div className="tw-pl-4 tw-mt-0.5 tw-space-y-0.5">
                  {groupUnits.map(unit => (
                    <CheckItem
                      key={unit.id}
                      label={[unit.name, unit.description].filter(Boolean).join(' - ')}
                      checked={selected.includes(unit.id)}
                      onChange={() => onToggle(unit.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          {groups.length === 0 && <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>}
        </>
      ) : (
        <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No sales units available</p>
      )}
    </FilterSection>
  );
};

SalesUnitFilter.propTypes = {
  units: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
    region: PropTypes.string
  })).isRequired,
  selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  search: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  onClear: PropTypes.func
};

const EMPTY_FILTERS = { sales_units: [], sales_incharges: [], customers: [] };

const ContractFilterSidebar = ({ filterOptions, loading, filters, onApply, onReset }) => {
  const [local,          setLocal]          = useState({ ...EMPTY_FILTERS, ...filters });
  const [unitSearch,     setUnitSearch]     = useState('');
  const [inchargeSearch, setInchargeSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');

  useEffect(() => { setLocal({ ...EMPTY_FILTERS, ...filters }); }, [filters]);

  const clearSection = (key) => {
    setLocal(prev => ({ ...prev, [key]: [] }));
  };

  const toggle = (key, value) => {
    setLocal(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const toggleGroup = (ids) => {
    setLocal(prev => {
      const current = prev.sales_units || [];
      const allOn   = ids.every(id => current.includes(id));
      return { ...prev, sales_units: allOn ? current.filter(id => !ids.includes(id)) : [...new Set([...current, ...ids])] };
    });
  };

  const handleReset = () => {
    setLocal(EMPTY_FILTERS);
    setUnitSearch('');
    setInchargeSearch('');
    setCustomerSearch('');
    onReset();
  };

  const activeCount = (local.sales_units?.length || 0) + (local.sales_incharges?.length || 0) + (local.customers?.length || 0);

  const opts       = filterOptions || {};
  const salesUnits = opts.sales_units     || [];
  const incharges  = useMemo(() => opts.sales_incharges || [], [opts.sales_incharges]);
  const customers  = useMemo(() => opts.customers       || [], [opts.customers]);


  const filteredIncharges = useMemo(
    () => incharges.filter(n => n.toLowerCase().includes(inchargeSearch.toLowerCase())),
    [incharges, inchargeSearch]
  );

  const filteredCustomers = useMemo(
    () => customers.filter(c => (c.name || '').toLowerCase().includes(customerSearch.toLowerCase())),
    [customers, customerSearch]
  );

  return (
    <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden tw-flex tw-flex-col" style={{ height: 'max(320px, calc(100dvh - var(--bs-top-nav-height, 4.3125rem) - 5.75rem))' }}>
      {/* Header */}
      <div className="tw-px-4 tw-py-3.5 tw-bg-gradient-to-r tw-from-blue-600 tw-to-primary-700 tw-text-white tw-flex tw-items-center tw-justify-between tw-flex-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiFilter size={15} />
          <span className="tw-font-semibold tw-text-sm">Filters</span>
          {activeCount > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-white tw-text-blue-700 tw-text-xs tw-font-bold">{activeCount}</span>
          )}
        </div>
        {activeCount > 0 && (
          <button type="button" onClick={handleReset} className="tw-text-xs tw-text-blue-100 hover:tw-text-white tw-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0 tw-leading-none">
            <FiRefreshCw size={11} /> Reset
          </button>
        )}
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-8 tw-flex-1"><LoadingSpinner size="sm" /></div>
      ) : (
        <div className="tw-p-3 tw-space-y-2 tw-flex-1 tw-overflow-y-auto">
          <SalesUnitFilter
            units={salesUnits}
            selected={local.sales_units || []}
            search={unitSearch}
            onSearch={setUnitSearch}
            onToggle={(id) => toggle('sales_units', id)}
            onToggleGroup={toggleGroup}
            onClear={() => clearSection('sales_units')}
          />

          <FilterSection title="Sales Incharge" count={local.sales_incharges?.length || 0} onClear={() => clearSection('sales_incharges')} defaultOpen={(local.sales_incharges?.length || 0) > 0}>
            {incharges.length > 0 ? (
              <>
                <SearchInput value={inchargeSearch} onChange={setInchargeSearch} placeholder="Search incharge…" />
                {filteredIncharges.map(name => (
                  <CheckItem
                    key={name}
                    label={name}
                    checked={(local.sales_incharges || []).includes(name)}
                    onChange={() => toggle('sales_incharges', name)}
                  />
                ))}
                {filteredIncharges.length === 0 && <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>}
              </>
            ) : (
              <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No data available</p>
            )}
          </FilterSection>

          <FilterSection title="Customer" count={local.customers?.length || 0} onClear={() => clearSection('customers')} defaultOpen={(local.customers?.length || 0) > 0}>
            {customers.length > 0 ? (
              <>
                <SearchInput value={customerSearch} onChange={setCustomerSearch} placeholder="Search customer…" />
                {filteredCustomers.map(c => (
                  <CheckItem
                    key={c.id}
                    label={c.name}
                    checked={(local.customers || []).includes(c.id)}
                    onChange={() => toggle('customers', c.id)}
                  />
                ))}
                {filteredCustomers.length === 0 && <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-2">No results</p>}
              </>
            ) : (
              <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No data available</p>
            )}
          </FilterSection>
        </div>
      )}

      <div className="tw-px-3 tw-py-3 tw-border-t tw-border-gray-100 tw-flex-shrink-0" style={{ boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.05)' }}>
        <Button onClick={() => onApply(local)} className="tw-w-full tw-justify-center tw-py-2 tw-text-sm tw-font-semibold tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-rounded">
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

ContractFilterSidebar.propTypes = {
  filterOptions: PropTypes.object,
  loading:       PropTypes.bool,
  filters:       PropTypes.object,
  onApply:       PropTypes.func.isRequired,
  onReset:       PropTypes.func.isRequired
};
ContractFilterSidebar.defaultProps = { filterOptions: null, loading: false, filters: {} };

export default ContractFilterSidebar;
